import { Suspense, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Bell, ChevronDown, LogOut, Menu } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { dashboardNav } from '@/lib/nav';
import { Button } from '@/components/ui';
import { BrandMark } from '@/components/Brand';
import { InstallButton } from '@/components/Pwa';
import { RouteFallback } from '@/components/common';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ThemeToggle } from '@/lib/theme';
import { cn, humanize } from '@/lib/utils';

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

export function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Menus are scoped per role so different roles never share the same sidebar.
  // A SUPER_ADMIN always sees every item.
  const roles = user?.roles ?? [];
  const isSuperAdmin = user?.isSuperAdmin ?? false;
  const groups = useMemo(
    () =>
      dashboardNav
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) =>
              isSuperAdmin || item.roles.length === 0 || item.roles.some((role) => roles.includes(role)),
          ),
        }))
        .filter((group) => group.items.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id, isSuperAdmin, roles.join(',')],
  );

  // Resolve the deepest matching nav item for the breadcrumb/page title.
  const current = useMemo(() => {
    const items = groups.flatMap((group) =>
      group.items.map((item) => ({ ...item, group: group.label })),
    );
    return items
      .filter(
        (item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`),
      )
      .sort((a, b) => b.to.length - a.to.length)[0];
  }, [groups, location.pathname]);

  const hasNotifications = groups.some((group) =>
    group.items.some((item) => item.to === '/app/notifikasi'),
  );

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  async function handleLogout() {
    await logout();
    navigate('/masuk');
  }

  const sidebar = (
    <div className="flex h-full flex-col">
      <Link to="/app" className="flex items-center gap-3 px-4 pb-4 pt-5">
        <BrandMark className="h-10 w-10 shadow-sm" />
        <span className="leading-tight">
          <span className="block whitespace-nowrap text-sm font-bold tracking-tight">
            TIRTA SMART
          </span>
          <span className="block whitespace-nowrap text-[11px] text-muted-foreground">
            Panel Internal Desa
          </span>
        </span>
      </Link>

      <nav className="flex-1 space-y-4 overflow-y-auto px-3 pb-6 scrollbar-thin">
        {groups.map((group) => (
          <div key={group.label}>
            <p className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">
              {group.label}
            </p>
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.to === '/app'}>
                  {({ isActive }) => (
                    <span
                      className={cn(
                        'group/nav relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-tirta-50 font-semibold text-tirta-800 dark:bg-tirta-950/50 dark:text-tirta-200'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                      )}
                    >
                      {isActive ? (
                        <span className="absolute -left-3 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-primary" />
                      ) : null}
                      <item.icon
                        className={cn(
                          'h-4 w-4 shrink-0 transition-colors',
                          isActive ? 'text-primary' : 'text-muted-foreground/80 group-hover/nav:text-foreground',
                        )}
                      />
                      {item.label}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-border p-3">
        <InstallButton className="w-full justify-start" variant="outline" size="sm" />
        <p className="mt-2 px-1 text-[11px] text-muted-foreground">TIRTA SMART · Desa Tirtasari</p>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-dvh bg-muted/40">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 border-r border-border bg-card lg:block">
        <div className="sticky top-0 h-dvh">{sidebar}</div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm animate-fade-in"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute left-0 top-0 h-full w-72 border-r border-border bg-card shadow-pop animate-slide-up">
            {sidebar}
          </aside>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-3 border-b border-border bg-card/80 px-4 backdrop-blur-md pt-safe">
          <div className="flex min-w-0 items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Buka menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div className="min-w-0">
              {current?.group ? (
                <p className="hidden text-[11px] font-medium uppercase tracking-wider text-muted-foreground sm:block">
                  {current.group}
                </p>
              ) : null}
              <h1 className="truncate text-base font-semibold tracking-tight">
                {current?.label ?? 'Dasbor'}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <InstallButton showLabel={false} variant="ghost" size="icon" className="hidden sm:inline-flex" />
            {hasNotifications ? (
              <Button asChild variant="ghost" size="icon" title="Notifikasi" aria-label="Notifikasi">
                <Link to="/app/notifications">
                  <Bell className="h-4 w-4" />
                </Link>
              </Button>
            ) : null}
            <ThemeToggle />

            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  className="ml-0.5 flex items-center gap-2 rounded-full p-0.5 pr-1.5 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label="Menu akun"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-tirta-100 to-tirta-200 text-sm font-semibold text-tirta-800 dark:from-tirta-900 dark:to-tirta-950 dark:text-tirta-200">
                    {user ? initials(user.fullName) : '?'}
                  </span>
                  <ChevronDown className="hidden h-4 w-4 text-muted-foreground sm:block" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={8}
                  className="z-50 w-60 rounded-xl border border-border bg-card p-1.5 shadow-pop animate-slide-up"
                >
                  <div className="px-2.5 py-2">
                    <p className="truncate text-sm font-semibold">{user?.fullName}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {user ? user.roles.map((role) => humanize(role)).join(', ') : ''}
                    </p>
                  </div>
                  <DropdownMenu.Separator className="my-1 h-px bg-border" />
                  <DropdownMenu.Item asChild>
                    <Link
                      to="/"
                      className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm outline-none transition-colors data-[highlighted]:bg-muted"
                    >
                      Portal publik
                    </Link>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onSelect={() => void handleLogout()}
                    className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-destructive outline-none transition-colors data-[highlighted]:bg-destructive/10"
                  >
                    <LogOut className="h-4 w-4" /> Keluar
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        </header>

        <main className="flex-1 space-y-6 p-4 sm:p-6">
          <Suspense fallback={<RouteFallback />}>
            <ErrorBoundary resetKey={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </Suspense>
        </main>

        <footer className="border-t border-border px-4 py-3 text-center text-xs text-muted-foreground">
          TIRTA SMART · Satu Desa, Satu Sistem, Maju Bersama
        </footer>
      </div>
    </div>
  );
}
